import {TranslationModuleResult} from "./models/TranslationModuleResult";
import {TranslationModuleName} from "./models/TranslationModuleName";
import {GlobalState} from "../global/GlobalState";
import {graphQLServices} from "../services/GraphQLServices";
import {GET_TRANSLATIONS_PER_MODULE, PUT_MISSING_TRANSLATION_ITEMS} from "./Queries";
import {Translation} from "./models/Translation";
import {LanguageEvents} from "./Translations";
import {MissingTranslationItem} from "./models/MissingTranslationItem";
import {Configuration} from "../global/Configuration";

class TranslationService {
	private translationModules: Map<string, TranslationModuleResult> = new Map<string, TranslationModuleResult>();
	private modulesLoadedLock: Set<string> = new Set<string>();
	private modulesLoadingLock: Set<string> = new Set<string>();
	private mainModule: TranslationModuleName = "SureBasePortalMain";
	
	translateString(moduleName: TranslationModuleName, text: string | null | undefined): string {
		if (!text)
			return "";
		
		const modules = moduleName === this.mainModule ? [moduleName] : [moduleName, this.mainModule];
		const translation = this.translateStringFromMultipleModules(modules, text);
		
		if (translation === null) {
			const languageCode: string = GlobalState.userPreferences.languageKeyRequired;
			this.enqueueUnknownTranslation(languageCode, moduleName, text);
			return text;
		} else if (translation === undefined)
			return text;
		
		return translation;
		
	}
	
	private timer: number | undefined = undefined;
	private unknownTranslations: MissingTranslationItem[] = [];
	private allUnknownTranslations: Set<string> = new Set<string>();
	
	private enqueueUnknownTranslation(languageCode: string, moduleName: string, phrase: string) {
		if (languageCode === "en-US" || !Configuration.autoTranslate)
			return;
		const key = `${languageCode}|${moduleName}|${phrase}`;
		if (this.allUnknownTranslations.has(key))
			return;
		this.unknownTranslations.push({languageCode, moduleName, phrase});
		this.allUnknownTranslations.add(key);
		console.log(phrase);
		window.clearTimeout(this.timer);
		this.timer = window.setTimeout(() => this.processUnknownTranslations(), 1);
	}
	
	private processUnknownTranslations() {
		graphQLServices.translationsClient.mutate({
			mutation: PUT_MISSING_TRANSLATION_ITEMS,
			variables: {
				translations: Array.from(this.unknownTranslations),
			}
		});
		this.unknownTranslations = [];
		
		// todo trigger event to tell listeners that the translations have been changed
	}
	
	clear() {
		this.translationModules.clear();
		this.modulesLoadedLock.clear();
	}
	
	private translateStringFromMultipleModules(moduleNames: TranslationModuleName[], text: string | null): string | undefined | null {
		if (!text)
			return "";
		const debug = text === "OPEN";
		
		if (debug) console.log(`loading translation for text '${text}'`);
		const languageCode: string = GlobalState.userPreferences.languageKeyRequired;
		
		for (const moduleName of moduleNames) {
			const moduleLangKey = TranslationService.getModuleCacheKey(languageCode, moduleName);
			const moduleResult = this.translationModules.get(moduleLangKey);
			
			// if the module is not yet in memory, load it and return the default text
			// once the module is loaded, a ui refresh will happen and ask again for the translations
			if (!moduleResult) {
				if(this.modulesLoadingLock.has(moduleLangKey))
					return undefined;
				if (!this.modulesLoadedLock.has(moduleLangKey)) {
					// noinspection JSIgnoredPromiseFromCall
					this.loadTranslationAsync(languageCode, moduleName);
					return undefined;
				}
			}
			
			const result = moduleResult?.items?.get(text);
			if (debug) console.log(`loaded translation for text '${text}', '${result}'`);
			if (result)
				return result;
		}
		
		// potentially keep a Set<string> in memory to avoid logging warnings for the same text values
		// if (languageCode !== "en-gb")
		// console.log("missing translation item", languageCode, moduleNames, text);
		if (debug) {
			console.log(`failed to load translation for text '${text}'`);
			console.log("translationModules", this.translationModules);
		}
		
		return null;
	}
	
	private async loadTranslationAsync(languageCode: string, moduleName: TranslationModuleName): Promise<void> {
		const moduleCacheKey = TranslationService.getModuleCacheKey(languageCode, moduleName);
		if (!this.translationModules.has(moduleCacheKey)) {
			if (this.modulesLoadedLock.has(moduleCacheKey))
				return;
			this.modulesLoadedLock.add(moduleCacheKey);
			this.modulesLoadingLock.add(moduleCacheKey);
			const remoteResult = await TranslationService.getTranslationPerModuleAsync(languageCode, moduleName);
			this.modulesLoadingLock.delete(moduleCacheKey);
			this.translationModules.set(moduleCacheKey, remoteResult);
			const changeHash = performance.now().toString();
			LanguageEvents.onChange.emit({hash: changeHash});
		}
	}
	
	private static getModuleCacheKey(languageCode: string, module: string) {
		return languageCode + module;
	}
	
	private static async getTranslationPerModuleAsync(languageCode: string, module: TranslationModuleName): Promise<TranslationModuleResult> {
		const existsItems = await graphQLServices.translationsClient.query(
			{
				query: GET_TRANSLATIONS_PER_MODULE,
				variables: {
					languageCode: languageCode,
					module: module
				},
				fetchPolicy: "no-cache"
			}
		);
		const items: Translation[] | undefined = existsItems.data.translations?.translations;
		return {
			items: items?.associateBy(it => it.key, it => it.value) ?? new Map<string, string>(),
			languageCode: languageCode,
			moduleName: module
		};
	}
}

export const translationService = new TranslationService();
export const translateString = translationService.translateString.bind(translationService);

export function translateFunction(module: TranslationModuleName = "SureBasePortalMain") {
	return (key: string | null | undefined) => translateString(module, key);
}
